// Cave84 — TastingNoteModal (Phase 7)
// Spec §4.9 Consumption & Tasting Notes.
// Lets the owner add or edit the drinking location / tasting note / score
// for an already-consumed bottle — either to expand on what OpenBottleModal
// captured, or to backfill bottles that entered the cellar pre-consumed
// (e.g. bulk MWC import) and never had a ConsumptionEvent at all.

import { useState } from 'react';
import type { Bottle } from '@cave84/shared-types';
import { useUpsertTastingNote } from '@/hooks/useBottles';
import type { ConsumptionRecord } from '@/services/consumption';

interface Props {
  bottle: Bottle;
  wineId: string | undefined;
  existing: ConsumptionRecord;
  onClose: () => void;
}

const LOCATION_PRESETS = ['Home', 'Restaurant', "Friend's place", 'Tasting / event'];

export default function TastingNoteModal({ bottle, wineId, existing, onClose }: Props) {
  const upsert = useUpsertTastingNote();

  const [location, setLocation] = useState(existing.event?.location ?? '');
  const [note, setNote] = useState(existing.tastingNote?.free_text ?? '');
  const [score, setScore] = useState(
    existing.tastingNote?.personal_score_100pt != null
      ? String(existing.tastingNote.personal_score_100pt)
      : ''
  );

  const isEdit = !!(existing.event || existing.tastingNote);

  async function handleSave() {
    const parsedScore = score.trim() ? Number(score) : undefined;
    await upsert.mutateAsync({
      bottleId: bottle.id,
      wineId,
      existing,
      location: location.trim() || undefined,
      note: note.trim() || undefined,
      personalScore100pt: Number.isFinite(parsedScore) ? parsedScore : undefined,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6 overflow-y-auto">
      <div className="bg-surface-1 rounded-xl border border-border-medium w-full max-w-md shadow-xl my-auto">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border-subtle">
          <h2 className="font-display text-2xl font-medium text-text-primary text-center mb-1">
            {isEdit ? 'Edit Tasting Note' : 'Add Tasting Note'}
          </h2>
          <p className="text-sm text-text-muted text-center">
            Where you drank it, how it tasted, your score.
          </p>
        </div>

        {/* Fields */}
        <div className="px-6 py-5 space-y-5">
          {/* Location */}
          <div>
            <label htmlFor="tasting-note-location" className="block text-base font-semibold text-text-primary mb-2">
              Where did you drink it?
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {LOCATION_PRESETS.map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setLocation(preset)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    location === preset
                      ? 'border-gold bg-gold/10 text-gold'
                      : 'border-border-medium bg-surface-2 text-text-secondary hover:border-border-strong'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <input
              id="tasting-note-location"
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g. Home, Restaurant Mizlala…"
              className="w-full px-4 py-3 rounded-md border border-border-medium bg-surface-0 text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:border-gold"
            />
          </div>

          {/* Tasting note */}
          <div>
            <label htmlFor="tasting-note-text" className="block text-base font-semibold text-text-primary mb-2">
              Tasting note <span className="font-normal text-text-muted">(optional)</span>
            </label>
            <textarea
              id="tasting-note-text"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={5}
              placeholder="Appearance, nose, palate, finish, food pairing, who was there…"
              className="w-full px-4 py-3 rounded-md border border-border-medium bg-surface-0 text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:border-gold resize-none"
            />
          </div>

          {/* Score */}
          <div>
            <label htmlFor="tasting-note-score" className="block text-base font-semibold text-text-primary mb-2">
              Score <span className="font-normal text-text-muted">(optional, 100-pt scale)</span>
            </label>
            <input
              id="tasting-note-score"
              type="number"
              min={0}
              max={100}
              value={score}
              onChange={e => setScore(e.target.value)}
              placeholder="e.g. 94"
              className="w-32 px-4 py-3 rounded-md border border-border-medium bg-surface-0 text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:border-gold"
            />
          </div>

          {upsert.isError && (
            <p className="text-sm text-red-400 text-center">
              {(upsert.error as Error).message}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={upsert.isPending}
            className="flex-1 px-4 py-3 rounded-md border border-border-medium bg-surface-2 text-text-primary text-base font-medium hover:bg-surface-3 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={upsert.isPending}
            className="flex-1 px-4 py-3 rounded-md bg-claret text-bg font-semibold text-base hover:bg-claret/90 transition-colors disabled:opacity-40"
          >
            {upsert.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
